import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  Menu,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../../../contexts/AuthContext';
import { useError } from '../../../contexts/ErrorContext';
import { useSessionClass } from '../../../contexts/SessionClassContext';
import { useSubjectsByClassId } from '../../../hooks/useSubjects';
import { useTeacher, Teacher } from '../../../hooks/useTeacher';
import {
  getTimetable,
  saveTimetable,
  TimetableEntry,
} from '../../../services/timetableService';

const ALL_DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

const DEFAULT_PERIOD_DURATION = 45;
const DEFAULT_BREAK_DURATION = 45;

interface TimetableConfig {
  days: string[];
}

const DEFAULT_CONFIG: TimetableConfig = {
  days: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
};

interface DayMeta {
  defaultPeriodDuration: number;
  hasManualResizeAfterPeriod1: boolean;
}

interface TimetableRow {
  id?: number;
  rowId: string;
  order: number;
  periodNumber: number;
  timeSlot: string;
  subjectId: string | number;
  teacherId: string | number;
  isBreak: boolean;
  breakName?: string;
  durationMinutes: number;
}

let rowIdCounter = 0;
const nextRowId = (): string => `row-${++rowIdCounter}-${Date.now()}`;

const getConfigStorageKey = (classId: number) => `timetable_config_${classId}`;
const getDayMetaStorageKey = (classId: number) => `timetable_day_meta_${classId}`;

const loadConfigFromStorage = (classId: number): TimetableConfig => {
  try {
    const raw = localStorage.getItem(getConfigStorageKey(classId));
    if (raw) {
      const parsed = JSON.parse(raw) as { days?: string[] };
      if (Array.isArray(parsed.days) && parsed.days.length > 0) {
        return {
          days: ALL_DAYS.filter((d) => parsed.days!.includes(d)),
        };
      }
    }
  } catch {
    // fall through to default
  }
  return { days: [...DEFAULT_CONFIG.days] };
};

const saveConfigToStorage = (classId: number, config: TimetableConfig) => {
  localStorage.setItem(getConfigStorageKey(classId), JSON.stringify(config));
};

const loadDayMetaFromStorage = (classId: number): Record<string, DayMeta> => {
  try {
    const raw = localStorage.getItem(getDayMetaStorageKey(classId));
    if (raw) {
      return JSON.parse(raw) as Record<string, DayMeta>;
    }
  } catch {
    // fall through
  }
  return {};
};

const saveDayMetaToStorage = (classId: number, meta: Record<string, DayMeta>) => {
  localStorage.setItem(getDayMetaStorageKey(classId), JSON.stringify(meta));
};

const formatTimeFromMinutes = (totalMinutes: number): string => {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  let hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

const parseTimeToMinutes = (timeStr: string): number | null => {
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && hours !== 12) hours += 12;
  else if (ampm === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const parseTimeSlotToMinutes = (timeSlot: string): number => {
  if (!timeSlot) return 0;
  const startPart = timeSlot.split('-')[0]?.trim() ?? '';
  return parseTimeToMinutes(startPart) ?? 0;
};

const parseTimeSlotRange = (
  timeSlot: string
): { startMinutes: number; endMinutes: number } | null => {
  if (!timeSlot.includes('-')) return null;
  const [startPart, endPart] = timeSlot.split('-').map((s) => s.trim());
  const startMinutes = parseTimeToMinutes(startPart);
  const endMinutes = parseTimeToMinutes(endPart);
  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) return null;
  return { startMinutes, endMinutes };
};

const formatTimeSlotRange = (startMinutes: number, endMinutes: number): string =>
  `${formatTimeFromMinutes(startMinutes)} - ${formatTimeFromMinutes(endMinutes)}`;

const getSlotDuration = (timeSlot: string): number | null => {
  const range = parseTimeSlotRange(timeSlot);
  if (!range) return null;
  return range.endMinutes - range.startMinutes;
};

const createBlankPeriod1Row = (): TimetableRow => ({
  rowId: nextRowId(),
  order: 1,
  periodNumber: 1,
  timeSlot: '',
  subjectId: '',
  teacherId: '',
  isBreak: false,
  durationMinutes: DEFAULT_PERIOD_DURATION,
});

const renumberRows = (rows: TimetableRow[]): TimetableRow[] => {
  let periodCount = 0;
  let breakCount = 0;
  return rows.map((row, index) => {
    if (row.isBreak) {
      breakCount += 1;
      return { ...row, order: index + 1, periodNumber: -breakCount };
    }
    periodCount += 1;
    return { ...row, order: index + 1, periodNumber: periodCount };
  });
};

const formatDayLabel = (day: string): string => day.charAt(0) + day.slice(1).toLowerCase();

const rebuildTimeChain = (rows: TimetableRow[]): TimetableRow[] => {
  if (rows.length === 0) return rows;
  const updated = rows.map((row) => ({ ...row }));
  const firstRange = parseTimeSlotRange(updated[0].timeSlot);
  if (!firstRange) return updated;
  const startMinutes = firstRange.startMinutes;
  updated[0] = {
    ...updated[0],
    timeSlot: formatTimeSlotRange(startMinutes, startMinutes + updated[0].durationMinutes),
  };
  return cascadeTimesFromIndex(updated, 0);
};

const cascadeTimesFromIndex = (rows: TimetableRow[], fromIndex: number): TimetableRow[] => {
  if (fromIndex < 0 || fromIndex >= rows.length) return rows;
  const updated = rows.map((row) => ({ ...row }));

  for (let i = fromIndex + 1; i < updated.length; i++) {
    const prevRange = parseTimeSlotRange(updated[i - 1].timeSlot);
    if (!prevRange) break;
    const duration = updated[i].durationMinutes;
    updated[i] = {
      ...updated[i],
      timeSlot: formatTimeSlotRange(prevRange.endMinutes, prevRange.endMinutes + duration),
    };
  }

  return updated;
};

const isValidTimeSlot = (timeSlot: string): boolean => parseTimeSlotRange(timeSlot) !== null;

const mapApiEntryToRow = (entry: TimetableEntry, order: number): TimetableRow => {
  const duration =
    getSlotDuration(entry.timeSlot) ??
    (entry.isBreak ? DEFAULT_BREAK_DURATION : DEFAULT_PERIOD_DURATION);
  return {
    id: entry.id,
    rowId: nextRowId(),
    order,
    periodNumber: entry.periodNumber,
    timeSlot: entry.timeSlot,
    subjectId: entry.subjectId ?? '',
    teacherId: entry.teacherId ?? '',
    isBreak: entry.isBreak,
    breakName: entry.breakName ?? '',
    durationMinutes: duration,
  };
};

const sortRowsChronologically = (rows: TimetableRow[]): TimetableRow[] =>
  [...rows].sort((a, b) => parseTimeSlotToMinutes(a.timeSlot) - parseTimeSlotToMinutes(b.timeSlot));

const pageWrapperSx = {
  height: 'calc(100vh - 110px)',
  maxHeight: 'calc(100vh - 110px)',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  maxWidth: 1500,
  mx: 'auto',
  width: '100%',
};
const pagePaperSx = {
  p: 1.5,
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  minHeight: 0,
};
const pageTitleSx = { fontSize: '1.25rem', fontWeight: 700, whiteSpace: 'nowrap' as const };
const primaryBtnSx = {
  backgroundColor: '#3092e3',
  '&:hover': { backgroundColor: 'primary.main' },
  height: 36,
};
const tableHeaderCellSx = {
  fontWeight: 'bold' as const,
  backgroundColor: 'primary.light',
  color: 'white',
  py: 0.8,
  fontSize: '0.95rem',
};
const tableBodyCellSx = { py: 0.5, verticalAlign: 'middle' as const };
const daySelectBtnSx = {
  borderRadius: '16px',
  textTransform: 'capitalize' as const,
  fontWeight: 600,
  fontSize: '0.8125rem',
  px: 1.5,
  minHeight: 32,
  height: 32,
  borderColor: '#3092e3',
  color: '#3092e3',
  '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(48, 146, 227, 0.04)' },
};
const scrollableTableContainerSx = {
  overflowY: 'auto' as const,
  overflowX: 'auto' as const,
  flex: '0 1 auto',
  minHeight: 0,
  mb: 1,
};

const getTeachersForSubject = (
  teachers: Teacher[],
  subjectId: string | number,
  subjects: { id: number; name: string; subjectCode: string }[]
): Teacher[] => {
  if (!subjectId) return [];
  const subject = subjects.find((s) => s.id === Number(subjectId));
  if (!subject) return [];
  const subjectName = subject.name.trim().toLowerCase();
  const subjectCode = subject.subjectCode.trim().toLowerCase();
  return teachers.filter((t) => {
    const dept = (t.department || '').trim().toLowerCase();
    const spec = (t.specialization || '').trim().toLowerCase();
    const deptList = dept.split(',').map((d) => d.trim());
    const specList = spec.split(',').map((s) => s.trim());
    return (
      deptList.some((d) => d === subjectName || d === subjectCode) ||
      specList.some((s) => s === subjectName || s === subjectCode)
    );
  });
};

interface SortableRowProps {
  rowId: string;
  children: (dragHandleProps: Record<string, any>) => React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = ({ rowId, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#e3f2fd' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      {children({ ...listeners })}
    </TableRow>
  );
};

const TimetableManagement: React.FC = () => {
  const { user } = useAuth();
  const schoolId = user?.schoolId;
  const { selectedSession, selectedClass } = useSessionClass();
  const sessionId = selectedSession?.id;

  const isSyntheticClass = useMemo(() => {
    if (!selectedClass) return false;
    const sec = (selectedClass as any).section ?? '';
    return (
      sec === '__GRADE_ALL__' ||
      sec === '__CLASS_ALL__' ||
      (selectedClass.id != null && selectedClass.id < 0)
    );
  }, [selectedClass]);

  const effectiveClassId =
    !isSyntheticClass && selectedClass?.id && selectedClass.id > 0 ? selectedClass.id : 0;

  const { showError, showSuccess } = useError();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string>('MONDAY');
  const [timetableRows, setTimetableRows] = useState<TimetableRow[]>([createBlankPeriod1Row()]);
  const [loading, setLoading] = useState(false);
  const [timetableConfig, setTimetableConfig] = useState<TimetableConfig>(DEFAULT_CONFIG);
  const [dayMetaByDay, setDayMetaByDay] = useState<Record<string, DayMeta>>({});

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [draftConfig, setDraftConfig] = useState<TimetableConfig>(DEFAULT_CONFIG);
  const [configDaysError, setConfigDaysError] = useState(false);

  const [deleteRowIndex, setDeleteRowIndex] = useState<number | null>(null);
  const [dayMenuAnchor, setDayMenuAnchor] = useState<null | HTMLElement>(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const dayMenuOpen = Boolean(dayMenuAnchor);

  const { data: subjects = [] } = useSubjectsByClassId(effectiveClassId, !effectiveClassId);

  const { useStaffByStaffAndClass } = useTeacher(schoolId ?? undefined);
  const teachersQuery = useStaffByStaffAndClass(sessionId, effectiveClassId);
  const teachers = teachersQuery.data || [];

  const activeDays = useMemo(
    () => ALL_DAYS.filter((d) => timetableConfig.days.includes(d)),
    [timetableConfig.days]
  );

  const currentDayMeta = useMemo(
    (): DayMeta =>
      dayMetaByDay[selectedDay] ?? {
        defaultPeriodDuration: DEFAULT_PERIOD_DURATION,
        hasManualResizeAfterPeriod1: false,
      },
    [dayMetaByDay, selectedDay]
  );

  const period1Ready = useMemo(() => {
    const period1 = timetableRows.find((r) => !r.isBreak && r.periodNumber === 1);
    return period1 != null && isValidTimeSlot(period1.timeSlot);
  }, [timetableRows]);

  const updateDayMeta = useCallback(
    (day: string, patch: Partial<DayMeta>) => {
      setDayMetaByDay((prev) => {
        const current = prev[day] ?? {
          defaultPeriodDuration: DEFAULT_PERIOD_DURATION,
          hasManualResizeAfterPeriod1: false,
        };
        const next = { ...prev, [day]: { ...current, ...patch } };
        if (effectiveClassId) {
          saveDayMetaToStorage(effectiveClassId, next);
        }
        return next;
      });
    },
    [effectiveClassId]
  );

  useEffect(() => {
    if (effectiveClassId) {
      setTimetableConfig(loadConfigFromStorage(effectiveClassId));
      setDayMetaByDay(loadDayMetaFromStorage(effectiveClassId));
    }
  }, [effectiveClassId]);

  useEffect(() => {
    if (activeDays.length > 0 && !activeDays.includes(selectedDay as (typeof ALL_DAYS)[number])) {
      setSelectedDay(activeDays[0]);
    }
  }, [activeDays, selectedDay]);

  const initializeBlankTimetable = useCallback(() => {
    setTimetableRows([createBlankPeriod1Row()]);
  }, []);

  const loadSavedTimetable = useCallback(async () => {
    if (!effectiveClassId) return;
    setLoading(true);
    try {
      const data = await getTimetable(effectiveClassId, selectedDay);
      if (data && data.length > 0) {
        const sorted = sortRowsChronologically(
          data.map((entry, index) => mapApiEntryToRow(entry, index + 1))
        );
        const renumbered = renumberRows(sorted);
        setTimetableRows(renumbered);

        const period1 = renumbered.find((r) => !r.isBreak && r.periodNumber === 1);
        if (period1) {
          const duration = getSlotDuration(period1.timeSlot);
          if (duration != null) {
            updateDayMeta(selectedDay, { defaultPeriodDuration: duration });
          }
        }
      } else {
        initializeBlankTimetable();
      }
    } catch (error: any) {
      console.error('Failed to fetch timetable:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to fetch timetable for this day';
      showError(message);
      initializeBlankTimetable();
    } finally {
      setLoading(false);
    }
  }, [effectiveClassId, selectedDay, initializeBlankTimetable, showError, updateDayMeta]);

  useEffect(() => {
    if (effectiveClassId) {
      loadSavedTimetable();
    } else {
      initializeBlankTimetable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveClassId, selectedDay, timetableConfig]);

  const handleSlotChange = (index: number, val: string) => {
    setTimetableRows((prev) => {
      let updated = prev.map((row, i) => (i === index ? { ...row, timeSlot: val } : { ...row }));
      const duration = getSlotDuration(val);
      if (duration != null) {
        updated[index] = { ...updated[index], durationMinutes: duration };
      }

      const isPeriod1Edit =
        !updated[index].isBreak && updated[index].periodNumber === 1 && index === 0;

      if (isPeriod1Edit && duration != null && !currentDayMeta.hasManualResizeAfterPeriod1) {
        updateDayMeta(selectedDay, { defaultPeriodDuration: duration });
      } else if (index > 0) {
        updateDayMeta(selectedDay, { hasManualResizeAfterPeriod1: true });
      }

      updated = cascadeTimesFromIndex(updated, index);
      return updated;
    });
  };

  const handleSubjectChange = (index: number, val: string | number) => {
    setTimetableRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], subjectId: val, teacherId: '' };
      return updated;
    });
  };

  const handleTeacherChange = (index: number, val: string | number) => {
    setTimetableRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], teacherId: val };
      return updated;
    });
  };

  const handleBreakNameChange = (index: number, val: string) => {
    setTimetableRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], breakName: val };
      return updated;
    });
  };

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTimetableRows((prev) => {
      const oldIndex = prev.findIndex((r) => r.rowId === active.id);
      const newIndex = prev.findIndex((r) => r.rowId === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const moved = arrayMove(prev, oldIndex, newIndex);
      const renumbered = renumberRows(moved);
      return rebuildTimeChain(renumbered);
    });
  }, []);

  const rowIds = useMemo(() => timetableRows.map((r) => r.rowId), [timetableRows]);

  const handleDeleteRow = (index: number) => {
    setDeleteRowIndex(index);
  };

  const handleConfirmDeleteRow = () => {
    if (deleteRowIndex === null) return;
    setTimetableRows((prev) => {
      const removed = prev.filter((_, i) => i !== deleteRowIndex);
      if (removed.length === 0) {
        return [createBlankPeriod1Row()];
      }
      const renumbered = renumberRows(removed);
      const cascadeFrom = Math.max(0, deleteRowIndex - 1);
      return cascadeTimesFromIndex(renumbered, cascadeFrom);
    });
    setDeleteRowIndex(null);
  };

  const handleCancelDeleteRow = () => {
    setDeleteRowIndex(null);
  };

  const handleAddPeriod = () => {
    if (!period1Ready) return;
    setTimetableRows((prev) => {
      const lastRow = prev[prev.length - 1];
      const lastRange = parseTimeSlotRange(lastRow.timeSlot);
      if (!lastRange) return prev;

      const periodCount = prev.filter((r) => !r.isBreak).length;
      const duration = currentDayMeta.defaultPeriodDuration;
      const newRow: TimetableRow = {
        rowId: nextRowId(),
        order: prev.length + 1,
        periodNumber: periodCount + 1,
        timeSlot: formatTimeSlotRange(lastRange.endMinutes, lastRange.endMinutes + duration),
        subjectId: '',
        teacherId: '',
        isBreak: false,
        durationMinutes: duration,
      };
      return renumberRows([...prev, newRow]);
    });
  };

  const handleAddBreak = () => {
    if (!period1Ready) return;
    setTimetableRows((prev) => {
      const lastRow = prev[prev.length - 1];
      const lastRange = parseTimeSlotRange(lastRow.timeSlot);
      if (!lastRange) return prev;

      const breakCount = prev.filter((r) => r.isBreak).length;
      const duration = DEFAULT_BREAK_DURATION;
      const newRow: TimetableRow = {
        rowId: nextRowId(),
        order: prev.length + 1,
        periodNumber: -(breakCount + 1),
        timeSlot: formatTimeSlotRange(lastRange.endMinutes, lastRange.endMinutes + duration),
        subjectId: '',
        teacherId: '',
        isBreak: true,
        breakName: breakCount === 0 ? 'Lunch Break' : 'Short Break',
        durationMinutes: duration,
      };
      return renumberRows([...prev, newRow]);
    });
  };

  const handleOpenConfigDialog = () => {
    setDraftConfig({ days: [...timetableConfig.days] });
    setConfigDaysError(false);
    setConfigDialogOpen(true);
  };

  const handleDraftDayToggle = (day: string) => {
    setDraftConfig((prev) => {
      const days = prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day];
      return { days };
    });
    setConfigDaysError(false);
  };

  const handleSaveConfig = () => {
    if (draftConfig.days.length === 0) {
      setConfigDaysError(true);
      return;
    }
    if (!effectiveClassId) return;

    const orderedDays = ALL_DAYS.filter((d) => draftConfig.days.includes(d));
    const newConfig: TimetableConfig = { days: orderedDays };

    saveConfigToStorage(effectiveClassId, newConfig);
    setTimetableConfig(newConfig);
    setConfigDialogOpen(false);

    if (!orderedDays.includes(selectedDay as (typeof ALL_DAYS)[number])) {
      setSelectedDay(orderedDays[0]);
    }
  };

  const handleSave = async () => {
    if (!effectiveClassId) {
      showError('Please select a specific class and section');
      return;
    }

    const invalidRow = timetableRows.find((r) => r.timeSlot && !isValidTimeSlot(r.timeSlot));
    if (invalidRow) {
      showError('One or more time slots have an invalid format. Use "HH:MM AM - HH:MM PM".');
      return;
    }

    const period1 = timetableRows.find((r) => !r.isBreak && r.periodNumber === 1);
    if (period1 && period1.timeSlot && !isValidTimeSlot(period1.timeSlot)) {
      showError('Period 1 must have a valid time slot before saving.');
      return;
    }

    setLoading(true);
    try {
      const payload: TimetableEntry[] = timetableRows
        .filter((r) => r.timeSlot && r.timeSlot.trim() !== '')
        .map((r) => ({
          id: r.id,
          classId: effectiveClassId,
          dayOfWeek: selectedDay as TimetableEntry['dayOfWeek'],
          periodNumber: r.periodNumber,
          timeSlot: r.timeSlot,
          subjectId: r.subjectId ? Number(r.subjectId) : null,
          teacherId: r.teacherId ? Number(r.teacherId) : null,
          isBreak: r.isBreak,
          breakName: r.isBreak ? r.breakName?.trim() || 'Break' : undefined,
        }));
      const savedEntries = await saveTimetable(effectiveClassId, selectedDay, payload);
      showSuccess('Timetable saved successfully');

      // Use the POST response data directly instead of making a redundant GET request
      if (savedEntries && savedEntries.length > 0) {
        const sorted = sortRowsChronologically(
          savedEntries.map((entry, index) => mapApiEntryToRow(entry, index + 1))
        );
        const renumbered = renumberRows(sorted);
        setTimetableRows(renumbered);
      } else {
        initializeBlankTimetable();
      }
    } catch {
      showError('Failed to save timetable');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    initializeBlankTimetable();
    updateDayMeta(selectedDay, {
      defaultPeriodDuration: DEFAULT_PERIOD_DURATION,
      hasManualResizeAfterPeriod1: false,
    });
  };

  const deleteRowLabel =
    deleteRowIndex !== null && timetableRows[deleteRowIndex]?.isBreak
      ? 'break'
      : deleteRowIndex !== null
        ? `Period ${timetableRows[deleteRowIndex]?.periodNumber}`
        : '';

  const renderEditTableBody = () =>
    timetableRows.map((row, index) => {
      const rowTeachers = getTeachersForSubject(teachers, row.subjectId, subjects);

      return (
        <SortableRow key={row.rowId} rowId={row.rowId}>
          {(dragHandleProps) => (
            <>
              <TableCell sx={{ ...tableBodyCellSx, width: 36, px: 0.5 }}>
                <Box
                  component="span"
                  {...dragHandleProps}
                  sx={{
                    cursor: 'grab',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    '&:active': { cursor: 'grabbing' },
                  }}
                  aria-label="Drag to reorder"
                >
                  <DragIndicatorIcon fontSize="small" />
                </Box>
              </TableCell>
              {row.isBreak ? (
                <>
                  <TableCell sx={{ ...tableBodyCellSx, fontWeight: 600, color: '#666' }}>Break</TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.timeSlot}
                      onChange={(e) => handleSlotChange(index, e.target.value)}
                      placeholder="09:00 AM - 09:45 AM"
                      sx={{ '& .MuiInputBase-root': { height: 32 } }}
                    />
                  </TableCell>
                  <TableCell colSpan={2} sx={tableBodyCellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.breakName ?? 'Break'}
                      onChange={(e) => handleBreakNameChange(index, e.target.value)}
                      placeholder="Break"
                      sx={{ '& .MuiInputBase-root': { height: 32 } }}
                    />
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={{ ...tableBodyCellSx, fontWeight: 600 }}>Period {row.periodNumber}</TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.timeSlot}
                      onChange={(e) => handleSlotChange(index, e.target.value)}
                      placeholder="09:00 AM - 09:45 AM"
                      sx={{ '& .MuiInputBase-root': { height: 32 } }}
                    />
                  </TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.subjectId || ''}
                        onChange={(e) => handleSubjectChange(index, e.target.value)}
                        displayEmpty
                        sx={{ height: 32 }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {subjects.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id}>
                            {sub.name} ({sub.subjectCode})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <FormControl fullWidth size="small" disabled={!row.subjectId}>
                      <Select
                        value={row.teacherId || ''}
                        onChange={(e) => handleTeacherChange(index, e.target.value)}
                        displayEmpty
                        sx={{ height: 32 }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {rowTeachers.map((tchr) => (
                          <MenuItem key={tchr.id} value={tchr.id}>
                            {tchr.firstName} {tchr.lastName || ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </>
              )}
              <TableCell align="center" sx={tableBodyCellSx}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleDeleteRow(index)}
                  aria-label={row.isBreak ? 'Delete break row' : `Delete period ${row.periodNumber}`}
                  sx={{ p: '4px' }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </>
          )}
        </SortableRow>
      );
    });

  const renderViewTableBody = () =>
    timetableRows.map((row, index) => {
      if (row.isBreak) {
        return (
          <TableRow key={`break-view-${index}`} sx={{ backgroundColor: '#f9f9f9' }}>
            <TableCell sx={{ ...tableBodyCellSx, fontWeight: 600, color: '#666' }}>Break</TableCell>
            <TableCell sx={tableBodyCellSx}>{row.timeSlot || '-'}</TableCell>
            <TableCell
              colSpan={2}
              align="center"
              sx={{ ...tableBodyCellSx, fontWeight: 600, color: '#777', fontStyle: 'italic' }}
            >
              {row.breakName || 'Break'}
            </TableCell>
          </TableRow>
        );
      }

      const selectedSubject = subjects.find((s) => s.id === Number(row.subjectId));
      const selectedTeacher = teachers.find((tchr) => tchr.id === Number(row.teacherId));

      return (
        <TableRow key={`period-view-${row.periodNumber}-${index}`}>
          <TableCell sx={{ ...tableBodyCellSx, fontWeight: 600 }}>Period {row.periodNumber}</TableCell>
          <TableCell sx={tableBodyCellSx}>{row.timeSlot || '-'}</TableCell>
          <TableCell sx={tableBodyCellSx}>
            {selectedSubject ? `${selectedSubject.name} (${selectedSubject.subjectCode})` : '-'}
          </TableCell>
          <TableCell sx={tableBodyCellSx}>
            {selectedTeacher
              ? `${selectedTeacher.firstName} ${selectedTeacher.lastName || ''}`
              : '-'}
          </TableCell>
        </TableRow>
      );
    });

  return (
    <>
      <Box sx={pageWrapperSx}>
        <Paper sx={pagePaperSx}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={pageTitleSx}>
              Time Table Management
            </Typography>
            {selectedClass && (
              <Button
                variant="contained"
                size="small"
                startIcon={<SettingsOutlinedIcon fontSize="small" />}
                onClick={handleOpenConfigDialog}
                sx={primaryBtnSx}
              >
                Configure Timetable
              </Button>
            )}
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_e, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: 36,
              mb: 1,
              flexShrink: 0,
              '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.875rem' },
            }}
          >
            <Tab label="Create / Edit" />
            <Tab label="View" />
          </Tabs>

          <Box sx={{ mb: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setDayMenuAnchor(e.currentTarget)}
              sx={daySelectBtnSx}
            >
              {formatDayLabel(selectedDay)}
            </Button>
            <Menu
              anchorEl={dayMenuAnchor}
              open={dayMenuOpen}
              onClose={() => setDayMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {activeDays.map((day) => (
                <MenuItem
                  key={day}
                  selected={selectedDay === day}
                  onClick={() => {
                    setSelectedDay(day);
                    setDayMenuAnchor(null);
                  }}
                  sx={{ textTransform: 'capitalize', minWidth: 160 }}
                >
                  {selectedDay === day ? (
                    <CheckIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                  ) : (
                    <Box component="span" sx={{ width: 28, display: 'inline-block' }} />
                  )}
                  {formatDayLabel(day)}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {!selectedClass ? (
            <Alert severity="info" sx={{ py: 0.5, px: 1.5, flexShrink: 0 }}>
              Please select a class to load the timetable.
            </Alert>
          ) : isSyntheticClass ? (
            <Alert severity="warning" sx={{ py: 0.5, px: 1.5, flexShrink: 0 }}>
              Please select a specific class and section (e.g., &quot;2nd - A&quot;) from the header
              dropdown to manage its timetable. &quot;ALL&quot; selections are not supported for
              timetable configuration.
            </Alert>
          ) : loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1} py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : activeTab === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                  <TableContainer component={Paper} sx={scrollableTableContainerSx}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...tableHeaderCellSx, width: 36, px: 0.5 }} />
                          <TableCell sx={tableHeaderCellSx}>Period Number</TableCell>
                          <TableCell sx={tableHeaderCellSx}>Time Slot</TableCell>
                          <TableCell sx={tableHeaderCellSx}>Subject</TableCell>
                          <TableCell sx={tableHeaderCellSx}>Teacher</TableCell>
                          <TableCell sx={{ ...tableHeaderCellSx, width: 80 }} align="center">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>{renderEditTableBody()}</TableBody>
                    </Table>
                  </TableContainer>
                </SortableContext>
              </DndContext>

              {!period1Ready && (
                <Alert severity="info" sx={{ mb: 1, py: 0.5, px: 1.5, flexShrink: 0 }}>
                  Set Period 1&apos;s time slot before adding more periods or breaks.
                </Alert>
              )}

              <Box display="flex" gap={1} mb={1} justifyContent="flex-end" flexShrink={0}>
                <Tooltip
                  title={
                    period1Ready
                      ? 'Add a new period after the last block'
                      : 'Set Period 1 time slot first'
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={handleAddPeriod}
                      disabled={!period1Ready}
                      sx={{ height: 36, borderColor: '#3092e3', color: '#3092e3' }}
                    >
                      Add Period
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    period1Ready
                      ? 'Add a break after the last block'
                      : 'Set Period 1 time slot first'
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={handleAddBreak}
                      disabled={!period1Ready}
                      sx={{ height: 36, borderColor: '#3092e3', color: '#3092e3' }}
                    >
                      Add Break
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Box display="flex" gap={1} flexShrink={0} justifyContent="flex-start">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon fontSize="small" />}
                  onClick={handleSave}
                  sx={primaryBtnSx}
                >
                  Save Timetable
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteSweepIcon fontSize="small" />}
                  onClick={handleClear}
                  sx={{ height: 36 }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <TableContainer component={Paper} sx={scrollableTableContainerSx}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderCellSx}>Period Number</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Time Slot</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Subject</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Teacher</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{renderViewTableBody()}</TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Configure Timetable
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
            Days of Week
          </Typography>
          <FormGroup>
            <Grid container spacing={1}>
              {ALL_DAYS.map((day) => (
                <Grid item xs={6} sm={4} key={day}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={draftConfig.days.includes(day)}
                        onChange={() => handleDraftDayToggle(day)}
                        sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                      />
                    }
                    label={day.charAt(0) + day.slice(1).toLowerCase()}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
          {configDaysError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              At least one day must be selected.
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Saving will enable the selected days. Each day starts with Period 1 — add more periods
            and breaks from the Create / Edit tab.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveConfig}
            variant="contained"
            color="primary"
            disabled={draftConfig.days.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteRowIndex !== null}
        onClose={handleCancelDeleteRow}
        aria-labelledby="delete-row-dialog-title"
      >
        <DialogTitle id="delete-row-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this {deleteRowLabel} row from the timetable?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteRow} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteRow} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TimetableManagement;
